/**
 * Role helpers.
 *
 * The backend's user payload can express the role in a few shapes depending on
 * the endpoint/serializer (a plain `role` string, a `roles` array, Spring
 * `authorities`, or a `userType`/`type` field), so we read it defensively and
 * normalise to an upper-case string without the Spring `ROLE_` prefix.
 */

/** Pulls a normalised role string (e.g. "ADMIN", "USER", "PREMIUM") from a user payload. */
export function extractRole(user) {
  if (!user) return null;

  const clean = (v) =>
    typeof v === 'string' ? v.replace(/^ROLE_/i, '').trim().toUpperCase() : null;

  // Single string field.
  const single = clean(user.role) || clean(user.userType) || clean(user.type);
  if (single) return single;

  // Array of roles or Spring authorities ([{ authority }] or ["ROLE_ADMIN"]).
  const list = user.roles || user.authorities;
  if (Array.isArray(list) && list.length) {
    const values = list
      .map((r) => clean(typeof r === 'string' ? r : r?.authority || r?.role || r?.name))
      .filter(Boolean);
    // Prefer the most privileged if several are present.
    if (values.includes('ADMIN')) return 'ADMIN';
    if (values.length) return values[0];
  }

  // Explicit boolean flags as a last resort.
  if (user.admin === true || user.isAdmin === true) return 'ADMIN';

  return null;
}

/** True when the user is an administrator. */
export function isAdmin(user) {
  return extractRole(user) === 'ADMIN';
}

/**
 * True when the user may create/edit activities — the ADMIN and EDITOR roles.
 * Mirrors the backend rule guarding the `/activities` write endpoints.
 */
export function canManageActivities(user) {
  const role = extractRole(user);
  return role === 'ADMIN' || role === 'EDITOR';
}
